import React, { useEffect, useRef, useContext, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import PropTypes from "prop-types";

import { Box } from "../../boxes/Box";
import { Icon } from "../../icons/Icon";
import { Error } from "../../errors/Error";
import { ThemeContext } from "../../../utils";

import "./dropdown.scss";

/**
 * Dropdown
 *
 * Dropdown component
 *
 * @return {jsx}
 */
export const Dropdown = ({
  options,
  selected,
  setSelected,
  errorMessage,
  placeholder,
  disabled,
  isSmall = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useContext(ThemeContext);

  const sourceRef = useRef(null);
  const [width, setWidth] = useState(null);

  useEffect(() => {
    const updateWidth = () => {
      if (sourceRef.current) {
        const width = sourceRef.current.offsetWidth;
        setWidth(width);
      }
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [selected]);

  let placeholderText = placeholder || "Select";
  if (!placeholder || placeholder === "Select") {
    const language = localStorage.getItem("language");
    switch (language) {
      case "en":
        placeholderText = "Select";
        break;
      case "kk":
        placeholderText = "Таңдау";
        break;
      case "ru":
        placeholderText = "Выбрать";
        break;
      case "pl":
        placeholderText = "Wybierz";
        break;
      case "el":
        placeholderText = "Επιλογή";
        break;
      default:
        placeholderText = "Select";
    }
  }

  const selectedLabel =
    options.find((option) => option.value === selected)?.label || "";

  const handleOnClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleChooseOption = (option) => {
    setSelected(option);
    setIsOpen(false);
  };

  const renderAllOptions = () => {
    if (!isOpen) return <div />;
    return (
      options &&
      options.map((option, index) => {
        return (
          <li
            key={index}
            className={[
              "option-container",
              selected ? selected === option.value && "option-selected" : "",
              selected === option.value &&
                theme !== "light" &&
                "option-selected--dark",
              option.isDisabled && "disabled",
            ].join(" ")}
            tabIndex={0}
            role="menuitem"
            onKeyDown={(event) => {
              const { activeElement } = document;
              const key = event.key;
              if (key === "Enter") {
                handleChooseOption(option.value);
              } else if (key === "ArrowUp") {
                event.preventDefault();
                if (activeElement.previousElementSibling) {
                  activeElement.previousElementSibling.focus();
                }
              } else if (key === "ArrowDown") {
                event.preventDefault();
                if (activeElement.nextElementSibling) {
                  activeElement.nextElementSibling.focus();
                }
              }
            }}
            onClick={
              option.isDisabled
                ? () => {}
                : () => handleChooseOption(option.value)
            }
          >
            <p
              role="link"
              className="text dropdown-content__single-option"
              key={index}
            >
              {option.label}
            </p>
          </li>
        );
      })
    );
  };

  const [isFirstOpen, setIsFirstOpen] = useState(true);

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <div className={["dropdown-container", disabled && "disabled"].join(" ")}>
        <Box
          boxShadow={"1"}
          borderSize="md"
          classes={[
            "dropdown",
            theme !== "light" ? "dropdown--dark" : "",
            isOpen ? "dropdown--expanded" : "",
            disabled ? "dropdown--disabled" : "",
            isSmall ? "dropdown--small" : "",
          ]}
        >
          <div
            tabIndex={0}
            role="navigation"
            className={[
              "dropdown-wrapper",
              theme !== "light" && "dropdown-wrapper--dark",
              errorMessage ? "error" : "",
            ].join(" ")}
            onFocus={(e) => {
              e.stopPropagation();
              if (isFirstOpen) {
                handleOnClick();
              }
              setTimeout(() => {
                setIsFirstOpen(false);
              }, 150);
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              if (isOpen && !isFirstOpen) {
                setIsOpen(false);
                setTimeout(() => {
                  setIsFirstOpen(true);
                }, 150);
              } else {
                setIsOpen(true);
                setTimeout(() => {
                  setIsFirstOpen(false);
                }, 150);
              }
            }}
            ref={sourceRef}
          >
            {selected ? (
              <p
                className={[
                  "text dropdown-text",
                  theme !== "light" && "dropdown-text--dark",
                ].join(" ")}
              >
                {selectedLabel}
              </p>
            ) : (
              <p className="text placeholder">{placeholderText}</p>
            )}
            <Icon
              size="sm"
              name="arrow-chevron-down"
              color={
                theme === "light"
                  ? isOpen
                    ? "#373737"
                    : "#cbcbcb"
                  : isOpen
                    ? "#fff"
                    : "#cbcbcb"
              }
            />
          </div>
          <div
            className={[
              "dropdown-content",
              theme !== "light" && "dropdown-content--dark",
              isSmall ? "dropdown-content--small" : "",
            ].join(" ")}
            style={isSmall && width ? { width: `${width - 16}px` } : {}}
          >
            <ul role="menubar" className="dropdown-content__options-container">
              {renderAllOptions()}
            </ul>
          </div>
        </Box>
        {errorMessage && !disabled ? <Error message={errorMessage} /> : null}
      </div>
    </OutsideClickHandler>
  );
};

Dropdown.propTypes = {
  /**
   * Dropdown options
   */
  options: PropTypes.arrayOf(PropTypes.object),

  /**
   * The value of the selected option
   * @default null
   * */
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Set selected option
   * */
  setSelected: PropTypes.func,

  /**
   * Error message
   * */
  errorMessage: PropTypes.string,

  /**
   * Placeholder
   * */
  placeholder: PropTypes.string,

  /**
   * Disabled state
   */
  disabled: PropTypes.bool,

  /**
   * Is small size
   */
  isSmall: PropTypes.bool,
};

Dropdown.defaultProps = {
  options: [],
  selected: null,
  setSelected: () => {},
  errorMessage: "",
  placeholder: "Select",
  disabled: false,
  isSmall: false,
};
